import AddIcon from "@mui/icons-material/Add";
import { Grid, IconButton, Typography } from "@mui/material";
import {
  MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import getAxiosError from "../../../app/api/axiosError";
import { getfdpList } from "../../../app/features/fdp/fdpSlice";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import { AddParcelCenterFdpMappingRequest } from "../../../app/models/requestModels/parcelCenterRequest";
import { FdpResponse } from "../../../app/models/responseModels/FdpResponse";
import { ParcelCenterResponse } from "../../../app/models/responseModels/parcelCenterResponse";
import {
  useSubmitModal,
  useSubmitModalWithParameter,
} from "../../../components/common/modalPopup/useSubmitModal";
import { CustomTooltip } from "../../../components/common/tooltip/CustomToolTip";
import { PAGES } from "../../../enums/PagesEnum";
import { getDefaultMRTOptions } from "../../dedicatedDelivery/DefaultMrtOptions";
import AddFdpMappingModal from "./AddFdpMappingModal";
import EditFdpMappingModal from "./EditFdpMappingModal";

interface AssociatedFdpGridProps {
  data: FdpResponse[];
  parcelCenterData: ParcelCenterResponse;
  onComplete: CallableFunction;
}

const AssociatedFdpGrid: React.FC<AssociatedFdpGridProps> = ({
  data,
  parcelCenterData,
  onComplete,
}) => {
  const [clonedData, setClonedData] = useState<FdpResponse[]>(data);

  const defaultMRTOptions = getDefaultMRTOptions<FdpResponse>();

  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.PARCEL_CENTERS
    )
  );
  const IsCreatePermission = pagePermission
    ? pagePermission.isCreatePermission
    : false;

  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
          "Administrators"
        )
      : false
  );

  const { fdpList } = useSelector((state: RootState) => state.fdp);

  useEffect(() => {
    getAllFdps();
  }, []);

  const dispatch = useAppDispatch();

  // get all available FDP(s)
  const getAllFdps = useCallback(async () => {
    try {
      const response = await dispatch(getfdpList()).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message, {
          toastId: "fdpData",
        });
        return;
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "fdpDataException",
      });
    }
  }, [dispatch]);

  // Columns to be used on grid on landing page
  const columns = useMemo<MRT_ColumnDef<FdpResponse>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
      },
      {
        accessorKey: "companyName",
        header: "Company Name",
      },
      {
        accessorKey: "gcAccount",
        header: "Ground Cloud Account",
      },
      {
        accessorKey: "ranking",
        header: "Ranking",
      },
      {
        accessorKey: "address",
        header: "Address",
      },
      {
        accessorKey: "city",
        header: "City",
      },
      {
        accessorKey: "state",
        header: "State",
      },
      {
        accessorKey: "zipCode",
        header: "Zipcode",
      },
      {
        accessorKey: "email",
        header: "Email",
      },
      {
        accessorKey: "active",
        header: "Active",
        Cell: ({ cell, row }) => (
          <div>{<span>{row.original.active ? "True" : "False"}</span>}</div>
        ),
      },
    ],
    [fdpList]
  );

  const handleAfterNewMapping = (req: AddParcelCenterFdpMappingRequest) => {
    if (req && req.fdpMappings) {
      const updatedFdpMap = new Map<number, FdpResponse>(
        req.fdpMappings
          .map((e) => {
            const fdp = fdpList.find((f) => f.id == e.fdpId);
            if (fdp) {
              const updatedFdp = JSON.parse(JSON.stringify(fdp)) as FdpResponse;
              updatedFdp.ranking =
                typeof e.ranking === "number" ? e.ranking : parseInt(e.ranking);
              return [updatedFdp.id, updatedFdp] as [number, FdpResponse];
            }
            return null;
          })
          .filter((entry): entry is [number, FdpResponse] => entry !== null)
      );

      // Merge existing `clonedData` with the updated `fdp` objects
      const mergedData = clonedData.map((item) => {
        if (updatedFdpMap.has(item.id)) {
          return updatedFdpMap.get(item.id)!;
        }
        return item;
      });

      // Add any new `fdp` objects that are in the updated list but not in the existing data
      const newFdpData = Array.from(updatedFdpMap.values()).filter(
        (item) =>
          !clonedData.some((existingItem) => existingItem.id === item.id)
      );
      console.log([...mergedData, ...newFdpData]);
      // Set the state with the merged data and new entries
      setClonedData((prevResponse) => [...mergedData, ...newFdpData]);
    }
  };

  // open  modal logic  incase user tries to do new mapping
  const [mapNewFdp, openFdpMappingModal] = useSubmitModal(
    (closeFdpMappingModal) => (
      <AddFdpMappingModal
        closeModal={closeFdpMappingModal}
        parcelCenterId={parcelCenterData.parcelCenterId}
        onCompleted={handleAfterNewMapping}
      />
    )
  );

  // open  modal logic  incase user selects any of the store
  const [editExistingPcFdpMapping, openPcFdpMappingModal] =
    useSubmitModalWithParameter<FdpResponse>(
      (rowData, closeFdpMappingModal) => (
        <EditFdpMappingModal
          closeModal={closeFdpMappingModal}
          data={rowData}
          parcelCenterId={parcelCenterData.parcelCenterId}
          onCompleted={handleAfterNewMapping}
        />
      )
    );

  // preprare MRT V2 grid
  const table = useMaterialReactTable({
    ...defaultMRTOptions,
    columns: columns,
    data: clonedData,
    initialState: {
      ...defaultMRTOptions.initialState,
      columnVisibility: {
        id: true,
      },
      sorting: [{ desc: true, id: "id" }],
    },
    state: {
      showProgressBars: false,
      sorting: [{ desc: true, id: "id" }],
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (e) => {
        openPcFdpMappingModal(row.original);
      },
      ...defaultMRTOptions.muiTableBodyRowProps,
      sx: {
        cursor: "pointer",
      },
    }),
    renderTopToolbarCustomActions: ({ table }) =>
      IsCreatePermission &&
      IsAdmin && (
        <CustomTooltip
          arrow
          title="Map Fdp to Parcel Center"
          onClick={() => {
            openFdpMappingModal();
          }}
        >
          <IconButton type="button">
            <AddIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
      ),
    renderBottomToolbarCustomActions: () => {
      return (
        <Typography variant="subtitle2" component="p">
          Total Rows: {clonedData.length}
        </Typography>
      );
    },
  });

  return (
    <Grid item xs={12} sm={12} md={12} lg={12}>
      <MaterialReactTable table={table} />
      {mapNewFdp}
      {editExistingPcFdpMapping}
    </Grid>
  );
};

export default AssociatedFdpGrid;
